import { defineComponent, ref, Ref, computed, nextTick, onMounted, onUnmounted } from 'vue';
import { Listing, ListingTemplate, PreferenceType } from '@truckdown/locations';
import context from '@truckdown/components-systems';
import H from '@here/maps-api-for-javascript';

interface IData {
    bubbleMounted: Ref<boolean>,
    fullscreenLink: Ref<HTMLElement | null>,
    mapContainer: Ref<HTMLElement | null>,
    locationMarker: Ref<H.map.Marker | null>,
    bubbleEl: Ref<HTMLDivElement | null>
}

const iconPrefix = '/listings/locations-components/img/';
const iconSuffix = '-pin.png';

const getIconPreference = function (listing: Listing) {
    if (listing.properties.preference?.type == PreferenceType.Preferred) {
        return '-p';
    } else {
        return '';
    }
}

const customClasses = ['msp', 'mspar', 'kyrish', 'rechtien', 'border', 'bruckners'];

const getIconName = function (listing: Listing) {
    if (listing.properties.useCustomPin) {
        if (customClasses.some((c) => c == listing.properties.customClass)) {
            return listing.properties.customClass;
        }
    }

    switch (listing.properties.template) {
        case ListingTemplate.TDPreferred:
            return 'preferred';
        case ListingTemplate.Elite:
            return 'elite';
        case ListingTemplate.Pro:
            return 'pro';
        case ListingTemplate.Branded:
            return 'branded';
        default:
            return 'default';
    }
}

const getIcon = function (listing: Listing): H.map.Icon{
    let i = iconPrefix + getIconName(listing)
        + getIconPreference(listing) + iconSuffix;
        return new H.map.Icon(i);
}

const getInitialData = function (props: any): IData {
    return {
        mapContainer: ref<HTMLElement | null>(null),
        fullscreenLink: ref<HTMLElement | null>(null),
        locationMarker: ref<H.map.Marker | null>(null),
        bubbleMounted: ref<boolean>(false),
        bubbleEl: ref<HTMLDivElement | null>(null)
    };
};

export default defineComponent({
    name: 'listing-map',
    props: {
        listing: {
            type: Object as () => Listing,
            required: true
        }
    },
    emits: [],
    setup: function (props, ctx) {
        let hMap: H.Map | null = null;
        let data = getInitialData(props);

        const center = computed<H.geo.IPoint>(() => ({
            lat: props.listing.location.lat,
            lng: props.listing.location.lng
        }));

        const icon = computed<H.map.Icon>(() =>
            getIcon(props.listing)
        );

        let bubble: H.ui.InfoBubble | null = null;

        const openBubble = function (ui: H.ui.UI, position: { lat: number; lng: number }) {
            data.bubbleMounted.value = true;
            nextTick(() => {
                
                if (!data.bubbleEl.value) return;
                if (bubble) ui.removeBubble(bubble);

                bubble = new H.ui.InfoBubble(
                   position,
                    { content: data.bubbleEl.value } 
                );

                if (bubble) {
                    ui.addBubble(bubble);
                }
                
            });
        }

        const addLocationMarker = async function (map: H.Map, ui: H.ui.UI) {
            let marker = new H.map.Marker(
                center.value,
                {
                    volatility: true,
                    data: 'Marker data',
                    icon: icon.value
                }
            ) as H.map.Marker;
            map.addObject(marker);
            data.locationMarker.value = marker;
            map.getViewPort().resize();
            marker.addEventListener('tap', () => {
                openBubble(ui, center.value,
                );
            });
        
            marker.addEventListener("pointerenter", () => {
            map.getElement().style.cursor = "pointer";
            });

            marker.addEventListener("pointerleave", () => {
            map.getElement().style.cursor = "";
            });
        };


        const toggleFullScreen = async () => {
          const el = data.mapContainer.value as HTMLElement | null;
          if (!el) return;

          try {
            if (document.fullscreenElement === el) {
              await document.exitFullscreen();
              return;
            }

            if (el.requestFullscreen) {
              await el.requestFullscreen({ navigationUI: "show" } as any);
            }
          } catch (err) {
            console.warn("Fullscreen failed:", err);
          }
        };

        const addMapLayerButton = function(ui: H.ui.UI, normalLayer: H.map.layer.TileLayer, satLayer: H.map.layer.TileLayer) {
            ui.removeControl("mapsettings");
            ui.addControl("mapsettings", new H.ui.MapSettingsControl({
                baseLayers: [
                    { label: "Map", layer: normalLayer },
                    { label: "Satellite", layer: satLayer },
                ]
            }));

            const scalebar = ui.getControl("scalebar");
            if (scalebar) {
                ui.removeControl("scalebar");
                ui.addControl("scalebar", scalebar);
            }
        };

        const addFullscreenButton = function(ui: H.ui.UI) {
             const fsContainer = new H.ui.Control();
            fsContainer.setAlignment(H.ui.LayoutAlignment.TOP_RIGHT);
            const fsButton = new H.ui.base.Element(
                "button",
                "td-btn icon-only"
            );

            fsContainer.addChild(fsButton);

            ui.addControl("fullscreen", fsContainer);

            requestAnimationFrame(() => {
                const btn = fsButton.getElement() as HTMLButtonElement;
                if (!btn) return;

                btn.type = "button";
                btn.style.padding = "0";
                btn.style.fontSize = "18px";
                btn.style.fontWeight = "bold";
                btn.style.backgroundColor = "#fff";
           
                btn.innerHTML = "&#x26F6;";

                btn.addEventListener("click", (e) => {
                e.preventDefault();
                toggleFullScreen();
                hMap?.getViewPort().resize();
                });
            });
        }

        const setupDefaultMap = async function () {
            let platform: H.service.Platform = new H.service.Platform({
                apikey: context.settings['here-key']
            });

            const normalSvc = platform.getRasterTileService({
                queryParams: {
                    style: "explore.day",
                    size: 512,
                },
            });
            const satSvc = platform.getRasterTileService({
                queryParams: { style: "explore.satellite.day", size: 512 },
            });

            const normalLayer = new H.map.layer.TileLayer(new H.service.rasterTile.Provider(normalSvc));
            const satLayer = new H.map.layer.TileLayer(new H.service.rasterTile.Provider(satSvc));

            if (!data.mapContainer.value) {
                console.error("Map container is not ready yet");
                return;
            }

            let map = new H.Map(
                data.mapContainer.value,
                normalLayer,
                {
                    center: center.value,
                    zoom: 14,
                    pixelRatio: window.devicePixelRatio || 1,
                }
            );

            hMap = map;
            if (map) {
                let behaviour = new H.mapevents.Behavior(new H.mapevents.MapEvents(map));
                await nextTick();
                map.getViewPort().resize();
                window.addEventListener('resize', () => map.getViewPort().resize());
               
                try {
                    const ui = H.ui.UI.createDefault(map, {
                        raster: { normal: { map: normalLayer } }
                    } as any);

                    await addLocationMarker(map, ui);
                    await addMapLayerButton(ui, normalLayer, satLayer);
                    await addFullscreenButton(ui);
                    addCircleToMap(map);

                } catch (e) {
                    console.error("HERE UI init failed:", e);
                }
            }
        }

        const addCircleToMap = function (map: H.Map) {
            const circle = new H.map.Circle(center.value, 24140.16);
            circle.setStyle(
                new H.map.SpatialStyle({
                    strokeColor: "rgba(86, 89, 97, 1)",
                    fillColor: "rgba(0, 0, 0, 0.05)",
                    lineWidth: 2
                })
            );
            map.addObject(circle);
        };

        const handleFullScreenChange = function () {
            const el = data.mapContainer.value as HTMLElement | null;
            if (!el) return;
            const isFs = document.fullscreenElement === el;

            if (!isFs) {
            el.style.height = "320px";
            el.style.width = "auto";
            }

            hMap?.getViewPort().resize(); // IMPORTANT for HEREmaps
        };

        onMounted(async () => {
            setupDefaultMap();
            document.addEventListener("fullscreenchange", handleFullScreenChange);
        });

        onUnmounted(() => {
            document.removeEventListener("fullscreenchange", handleFullScreenChange);
        });
       
        return {
            ...data,
            center,
            icon
        };
    }
});
